import { Column, EmptyDiv, FormContext, InfoDiv } from '@/components/SettingsLayout';
import SkeletonList from '@/components/SkeletonLoader/SkeletonList';
import {
  useGetDefaultPricingConfigurationQuery,
  useGetPricingConfigurationQuery,
} from '@/store/slices/pricingConfigurationSlice';
import { useCreateOrUpdateCustomPlanMutation } from '@/store';
import { Text, Button, Icon, IconType } from '@/ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Company } from '@/context/types';
import { Plan } from '@/services/Plans/plans.service';
import PricingConfigurationForm from '@/pages/pricing-forms/PricingConfigurationForm';
import PricingDiffFooter from '@/pages/pricing-forms/PricingDiffFooter';
import { usePricingForm } from '@/pages/pricing-forms/usePricingForm';
import { channelValues, PricingConfigurationValues, toRates, versionOf } from '@/services/Pricing/pricing-configuration.service';
import { usePricingChannel } from '@/pages/pricing-forms/usePricingChannel';

type Props = {
  company: Company;
  plan: Plan;
};

function CompanyPricing({ company, plan }: Props) {
  const { t } = useTranslation();
  const [isReadOnly, setIsReadOnly] = useState(true);

  const orgContext = { organizationId: company.organizationId };
  const planContext = { organizationId: company.organizationId, planId: plan._id };

  const { data: orgPricing, isLoading: isOrgPricingLoading } =
    useGetPricingConfigurationQuery(orgContext);
  const { data: systemDefaults, isLoading: isDefaultsLoading } =
    useGetDefaultPricingConfigurationQuery(orgContext);
  const { data: existing, isLoading: isExistingLoading } =
    useGetPricingConfigurationQuery(planContext);

  const [createOrUpdateCustomPlan, { isLoading: isSaving }] = useCreateOrUpdateCustomPlanMutation();

  const { channel, setChannel } = usePricingChannel();
  const channelExisting = useMemo(() => channelValues(existing, channel), [existing, channel]);
  const existingRates = useMemo(() => toRates(existing), [existing]);

  // Narrowed to one channel before merging, or this spread would drop the other channel's rate.
  const baseline: PricingConfigurationValues = useMemo(
    () => ({
      ...channelValues(systemDefaults, channel),
      ...channelValues(orgPricing, channel),
    }),
    [systemDefaults, orgPricing, channel],
  );

  const { activeCountry, setActiveCountry, submitHandler, savedAt } = usePricingForm({
    baseline,
    existing: channelExisting,
    existingRates,
    channel,
    submit: (nextRates) =>
      createOrUpdateCustomPlan({
        organizationId: company.organizationId,
        companyId: company._id,
        pricingData: nextRates,
        // Undefined on the first save; the server accepts that unconditionally.
        expectedUpdatedAt: versionOf(existing),
      }),
    messages: {
      success: t('Messages.plan_update_success'),
      error: t('Messages.plan_update_error'),
      conflict: t('Messages.pricing_update_conflict'),
    },
  });

  if (isOrgPricingLoading || isDefaultsLoading || isExistingLoading) {
    return <SkeletonList />;
  }

  if (!systemDefaults) {
    return (
      <EmptyDiv>
        <Text size='s' weight='medium'>
          {t('Errors.error_loading_pricing')}
        </Text>
      </EmptyDiv>
    );
  }

  return (
    <FormContext submitHandler={submitHandler} mode='onChange' defaultValues={{ channel, rows: [] }}>
      <Column gapSize='24'>
        {isReadOnly && (
          <InfoDiv>
            <Text size='s' weight='light' color='base-grey'>
              {t('Pricing.organization_pricing_comment')}
            </Text>
            <Button type='button' variant='outline' onClick={() => setIsReadOnly(false)}>
              <Icon type={IconType.Pencil_Edit} width={24} height={24} />
              <Text size='s' weight='medium' color='base-grey'>
                {t('Pricing.customize')}
              </Text>
            </Button>
          </InfoDiv>
        )}
        <Column>
          <Text size='m' weight='semi-bold'>
            {t('Accounts.processing_fees')}
          </Text>
          <Text size='s' weight='light' color='base-grey'>
            {t('Accounts.processing_fees_comment')}
          </Text>
          <PricingConfigurationForm
            mode='company'
            defaults={baseline}
            orgPricing={baseline}
            existingValues={channelExisting}
            isReadOnly={isReadOnly}
            activeCountry={activeCountry}
            onActiveCountryChange={setActiveCountry}
            channel={channel}
            onChannelChange={setChannel}
            savedAt={savedAt}
          />
        </Column>
        <PricingDiffFooter
          baseline={baseline}
          existing={channelExisting}
          isSaving={isSaving}
          isReadOnly={isReadOnly}
          activeCountry={activeCountry}
        />
      </Column>
    </FormContext>
  );
}

export default CompanyPricing;
